import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
const faqs = [{
  question: 'How much does a website cost?',
  answer: 'Website pricing depends on the size, features, and complexity of the project. Simple websites typically start at $500, and larger sites that require online booking, e-commerce, custom forms, or advanced functionality go up from there. Contact us for a free quote based on your specific needs.'
}, {
  question: 'How long does it take to build a website?',
  answer: 'Most websites are completed within 3–4 days. Larger or more complex projects may require additional time depending on content, revisions, and requested features.'
}, {
  question: 'Do I need to provide the content?',
  answer: 'You can provide your own text and images, or we can assist with creating professional content and sourcing high-quality images for your website.'
}, {
  question: 'Will my website work on mobile devices?',
  answer: 'Yes. Every website we build is fully responsive and designed to work on desktops, tablets, and smartphones.'
}, {
  question: 'Can you redesign my existing website?',
  answer: 'Absolutely. I can update the appearance, improve functionality, enhance user experience, and optimize your existing website for better performance.'
}, {
  question: 'Do you offer website hosting?',
  answer: 'Yes. We offer website hosting plans that include server management, updates, security monitoring, and technical support.'
}, {
  question: 'What is included in website maintenance?',
  answer: 'Website maintenance may include software updates, security monitoring, backups, minor content changes, and technical support depending on your selected plan.'
}, {
  question: 'Will my website be optimized for Google?',
  answer: 'Yes. We build websites using SEO best practices, including proper page structure, mobile responsiveness, fast loading speeds, and search engine-friendly content.'
}, {
  question: 'Can I update the website myself?',
  answer: 'Yes. If you choose to host the website on your own hosting account, you will have full control and ownership of your website. Because our websites are built using modern AI-assisted development tools, making updates may require some familiarity with the platform or web design basics. If you prefer a hands-off experience, we also offer hosting and website management services where we handle updates, maintenance, security, and technical support for you.'
}, {
  question: 'Do you offer e-commerce websites?',
  answer: 'Yes. We can create online stores that allow you to sell products, accept payments, manage inventory, and process orders securely.'
}, {
  question: 'How many revisions are included?',
  answer: 'The number of revisions depends on the package selected. I work closely with clients throughout the design process to ensure satisfaction before launch. Typically up to 10 hours.'
}, {
  question: 'Do you require a deposit?',
  answer: 'Yes. A deposit is typically required before work begins. The remaining balance is due according to the agreed project terms.'
}, {
  question: "Who owns the website after it's completed?",
  answer: 'Once the project has been paid in full, you own the website design, content provided for the project, and associated files unless otherwise stated in the agreement. I typically manage it, but you own it 100% if you wanted to get outside hosting.'
}, {
  question: 'Can you help register a domain name?',
  answer: 'Yes. We can assist with domain registration or help connect an existing domain to your new website.'
}, {
  question: 'What if I need help after my website launches?',
  answer: 'I am available to provide ongoing support, maintenance, updates, and troubleshooting services after your website goes live with a hosting plan.'
}, {
  question: "Why should I choose Mike's Pro Web Design?",
  answer: "Mike's Pro Web Design provides personalized, one-on-one service with direct communication throughout the project. As a small business, I focus on affordable pricing, honest recommendations, responsive support, and creating websites that help businesses grow online."
}];
export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <section id="faq" className="py-24 bg-black border-y border-white/5" data-id="element-104">
      <div className="container mx-auto px-6 md:px-12" data-id="element-105">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-106">
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-white mb-6" data-id="element-107">
            Frequently Asked <span className="text-gradient" data-id="element-108">Questions</span>
          </h2>
          <p className="text-lg text-[#FFFFFF]" data-id="element-109">
            Got questions? I've got answers. Here are some of the most common
            things clients ask me.
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-4" data-id="element-110">
          {faqs.map((faq, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.05
        }} className="bg-[#111118] border border-white/5 rounded-xl overflow-hidden" data-id="element-111">
              <button onClick={() => setOpenIndex(openIndex === index ? null : index)} className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors" data-id="element-112">
                <span className="font-bold text-white pr-8" data-id="element-113">
                  {faq.question}
                </span>
                <ChevronDown className={`text-blue-500 transition-transform duration-300 flex-shrink-0 ${openIndex === index ? 'rotate-180' : ''}`} data-id="element-114" />
              </button>

              <AnimatePresence data-id="element-115">
                {openIndex === index && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3
            }} data-id="element-116">
                    <div className="px-6 pb-5 leading-relaxed border-t border-white/5 pt-4 text-[#FFFFFF]" data-id="element-117">
                      {faq.answer}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>)}
        </div>

        {/* Still Have Questions CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="max-w-3xl mx-auto mt-12 text-center bg-[#111118] border border-white/5 rounded-2xl p-10" data-id="element-118">
          <h3 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4" data-id="element-119">
            Still Have Questions?
          </h3>
          <p className="text-[#FFFFFF] mb-8 leading-relaxed" data-id="element-120">
            Contact Mike's Pro Web Design today for a free consultation and
            quote. We're happy to discuss your project and answer any questions
            you may have.
          </p>
          <a href="#contact" className="inline-flex items-center justify-center px-8 py-4 rounded-lg bg-gradient-to-r from-blue-600 to-blue-500 text-white font-medium text-lg hover:shadow-[0_0_30px_rgba(59,130,246,0.4)] transition-all" data-id="element-121">
            Get a Free Consultation
          </a>
        </motion.div>
      </div>
    </section>;
}